import React, {useCallback, useEffect, useState} from "react";
import { MenuItem } from "../../@types/stores/menu";
import { observer } from "mobx-react";
import "./style.scss";
import {getAllergenById, getHost} from "../../helpers/ApiHelper";
import defaultImg from "../../assets/images/default.webp";
import ModifiersGroup from "../MenuItem/ModifiersGroup";
import {OrderLine, OrderLineModifiers} from "../../@types/stores/order";
import lodash from "lodash";
import { useTranslation } from "react-i18next";
import orderStore from "../../stores/order";
import menuStore from "../../stores/menu";
import {useLocation, useNavigate, useParams} from "react-router";
import { moveToTop } from "../../helpers/DomHelper";
import useLayoutStyles from "../../hooks/styles";


export interface ModifiersProps {
    item: MenuItem;
    modifierGroupId?: number;
    skipRecommendations: boolean;
    isHalfAndHalf?: boolean;
}

const Modifiers = ({item, modifierGroupId, skipRecommendations, isHalfAndHalf}: ModifiersProps) => {
    const params = useParams();
    const [t] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const styles = useLayoutStyles();

    const [modifiers, setModifiers] = useState<OrderLineModifiers[]>([]);
    const [allowNext, setAllowNext] = useState<boolean>(true);

    useEffect(() => {
        if (orderStore.currentOrderLine?.modifiers?.length && !modifiers.length)
            setModifiers(orderStore.currentOrderLine.modifiers);
    }, [location]);

    useEffect(() => {
        checkAllowNext();
        if (modifiers.length) {
            const orderLine = orderStore.currentOrderLine;
            if (orderLine) {
                orderLine.modifiers = modifiers;
                orderStore.setCurrentOrderLine = orderLine;
            }
        }
    }, [modifiers]);

    const checkAllowNext = () => {
        let allow = true;
        if (modifierGroupId) {
            const group = item?.modifierGroups?.find(x => x.id === modifierGroupId);
            if (group) {
                const selectedModifierCount = modifiers.find(x => x.groupId === group.id)?.items.length || 0;
                const min = group.min || 0;
                if (selectedModifierCount < min)
                    allow = false;
            }
        }
        else {
            if (item.modifierGroups) {
                for (const group of item.modifierGroups) {
                    const selectedModifierCount = modifiers.find(x => x.groupId === group.id)?.items.length || 0;
                    const min = group.min || 0;
                    if (selectedModifierCount < min)
                        allow = false;
                }
            }
        }
        setAllowNext(allow);
    }

    const changeModifiers = (id: number, selectedModifiers: number[]) => {
        const _modifiers = lodash.cloneDeep(modifiers).filter((x: OrderLineModifiers) => x.groupId !== id);
        _modifiers.push({
            groupId: id,
            items: selectedModifiers
        });
        setModifiers(_modifiers);
    }

    const goNext = () => {
        if (!allowNext) return;

        moveToTop();

        // Menu article
        if (!skipRecommendations) {
            // if individual page
            if (modifierGroupId) {
                if (item.modifierGroups) {
                    const index = item.modifierGroups.findIndex(x => x.id === modifierGroupId);
                    // has next modifiers group
                    if (index >= 0 && item.modifierGroups.length - 1 > index) {
                        const nextGroupId = item.modifierGroups.at(index + 1)?.id;

                        if (nextGroupId) {
                            navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/modifiers/${nextGroupId}`);
                            return;
                        }
                    }
                    // go to first addons group
                    else {
                        if (item.addonGroups?.length) {
                            const firstAddonGroupId = item.addonGroups.at(0)?.id;
                            if (firstAddonGroupId) {
                                navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/addons/${firstAddonGroupId}`);
                                return;
                            }
                        }
                        else if (item.ingredientGroups?.length) {
                            navigate(`/menu/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/ingredients`);
                            return;
                        }
                    }
                }
            }
            // if group page
            else {
                if (item.addonGroups?.length) {
                    navigate(`/menu${isHalfAndHalf ? "/halfAndHalf" : ""}/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/addons`);
                    return;
                }
                else if (item.ingredientGroups?.length) {
                    navigate(`/menu${isHalfAndHalf ? "/halfAndHalf" : ""}/${menuStore.navigation.category}/${menuStore.navigation.subCategory}/${item.number}/ingredients`);
                    return;
                }
            }
        }
        // Recommendation article
        else {
            if (typeof params.groupId !== "undefined") {
                const groupId = params.groupId && !isNaN(+params.groupId) ? +params.groupId : 0;
                if (groupId) {
                    // if individual page
                    if (modifierGroupId) {
                        if (item.modifierGroups) {
                            const index = item.modifierGroups.findIndex(x => x.id === modifierGroupId);
                            // has next modifiers group
                            if (index >= 0 && item.modifierGroups.length - 1 > index) {
                                const nextGroupId = item.modifierGroups.at(index + 1)?.id;

                                if (nextGroupId) {
                                    navigate(`/recommendations/${groupId}/${item.number}/modifiers/${nextGroupId}`);
                                    return;
                                }
                            }
                            // go to first addons group
                            else {
                                if (item.addonGroups?.length) {
                                    const firstAddonGroupId = item.addonGroups.at(0)?.id;
                                    if (firstAddonGroupId) {
                                        navigate(`/recommendations/${groupId}/${item.number}/addons/${firstAddonGroupId}`);
                                        return;
                                    }
                                }
                                else if (item.ingredientGroups?.length) {
                                    navigate(`/recommendations/${groupId}/${item.number}/ingredients`);
                                    return;
                                }
                            }
                        }
                    }
                    // if group page
                    else {
                        if (item.addonGroups?.length) {
                            navigate(`/recommendations/${groupId}/${item.number}/addons`);
                            return;
                        }
                        else if (item.ingredientGroups?.length) {
                            navigate(`/recommendations/${groupId}/${item.number}/ingredients`);
                            return;
                        }
                    }
                }
            }
        }

        if (orderStore.currentOrderLine) {
            if (isHalfAndHalf) {
                if (menuStore.selectedHalfAndHalfStep >= 0) {
                    orderStore.currentOrderLine.count = 0.5;

                    const newArr: OrderLine[] = [...menuStore.selectedHalfAndHalfItems] as OrderLine[];
                    newArr[menuStore.selectedHalfAndHalfStep] = orderStore.currentOrderLine;
                    menuStore.selectedHalfAndHalfItems = newArr;

                    orderStore.currentOrderLine = null;
                }

                navigate(`/menu/halfAndHalf/${menuStore.halfAndHalfNavigation.category}/${menuStore.halfAndHalfNavigation.subCategory}`);
                return;
            }

            orderStore.addToOrder(orderStore.currentOrderLine);
        }

        if (menuStore.returnToScanArticle) {
            navigate("/scanArticle");

            return;
        }

        // from recommendations
        if (skipRecommendations) {
            if (menuStore.navigation.item) {
                const item = menuStore.getItemById(menuStore.navigation.item);

                if (item?.recommendedGroups?.length) {
                    const nextGroupIdIndex = item.recommendedGroups.findIndex(x => x === menuStore.navigation.recommendationGroupId) + 1;
                    if (nextGroupIdIndex && nextGroupIdIndex < item.recommendedGroups.length) {
                        const nextGroupId = item.recommendedGroups.at(nextGroupIdIndex);
                        navigate(`/recommendations/${nextGroupId}`);
                        return;
                    }
                }
            }

            navigate("/menu");
            return;
        }

        navigate("/recommendations");
    }

    const getSelectedModifiers = useCallback((id: number) => {
        return modifiers.find(x => x.groupId === id)?.items || [];
    }, [modifiers]);

    if (!item)
        return null;

    return <div className="Modifiers">
        <h2>{item.name}</h2>
        <div className="img" style={{backgroundImage: `url(${item.previewEdit ? `${getHost()}${item.previewEdit}` : (styles?.article.defaultEditImage ? `${getHost()}${styles.article.defaultEditImage}` : defaultImg)}`}} />
        <div className="description">{item.description || ""}</div>
        <div className="allergens">
            {item.allergens?.map(allergenId => getAllergenById(allergenId)?.name || null).filter(name => !!name).map(name => <div key={name} className="allergen">{name}</div>)}
        </div>
        <div className="modifiers">
            {modifierGroupId
                ? item.modifierGroups?.filter(mg => mg.id === modifierGroupId).map(mg => <ModifiersGroup key={`modifiersGroup_${mg.name}`} modifiersGroup={mg} onChange={changeModifiers} selected={getSelectedModifiers(mg.id)} />)
                : (item.modifierGroups?.length
                    ? item.modifierGroups.map(mg => <ModifiersGroup key={`modifiersGroup_${mg.name}`} modifiersGroup={mg} onChange={changeModifiers} selected={getSelectedModifiers(mg.id)} />)
                    : null)
            }
        </div>
        <div className={`button dark ${!allowNext ? "disable" : ""}`} onClick={goNext}>{t(modifiers.map(mg => mg.items).flat().length ? `next` : `skip`)}</div>
    </div>
}

export default observer(Modifiers);
